import Vapi from '@vapi-ai/web';
import { releasePreloadedVapiWebRtc } from '../../helpers/preloadVapiWebRtc.js';
import { ENCRYPTED_CONFIG } from './config.encrypted.js';

/** World id for /worlds/view?id=484 */
export const WORLD_ID = '484';

/** Gast Sitzplatz guestSeat in world 484 */
export const GUEST_SEAT_ID = '611ee96c-4c07-4104-bf57-fa39a54b1219';

/** Info element stop in world 484 */
export const INFO_ELEMENT_ID = '2d3f5a1a-625f-477e-93d7-8bd48e3ce915';

const TOUR_STEP = {
  GUEST_SEAT: 'guestSeat',
  INFO_ELEMENT: 'infoElement',
  DONE: 'done',
};

const GUIDE_FIRST_MESSAGE =
  'This is a guest seat. Click it to take a seat and talk with others in this room.';

const INFO_ELEMENT_MESSAGE =
  'This is an info element. Click it to open details about this space.';

const GUIDE_SYSTEM_PROMPT =
  'You are a bagless space guide. Keep replies short and clear. ' +
  'Do not greet with a generic "Hello" or small talk. ' +
  'First explain the guest seat (Gast Sitzplatz / "Take a seat"): ' +
  'users can sit there and talk with others. ' +
  'Later you will be asked to explain the info element: users can click it to open room information. ' +
  'Do not invent other objects unless asked.';

const LOOK_AT_DURATION = 1.5;
const OVERLAY_ID = 'room-guide-484-start';

function isPlaceholderKey(key) {
  return !key
    || key === 'YOUR_VAPI_PUBLIC_KEY'
    || key === 'your_vapi_public_key_here';
}

function getInlineAssistant() {
  return {
    transcriber: {
      provider: 'deepgram',
      model: 'nova-2',
      language: 'en',
      disablePartialTranscripts: false,
    },
    model: {
      provider: 'openai',
      model: 'gpt-4o-mini',
      messages: [{ role: 'system', content: GUIDE_SYSTEM_PROMPT }],
    },
    voice: {
      provider: '11labs',
      voiceId: '21m00Tcm4TlvDq8ikWAM',
    },
    firstMessage: GUIDE_FIRST_MESSAGE,
    clientMessages: ['speech-update', 'transcript', 'hang', 'tool-calls'],
  };
}

function getAssistantOverrides() {
  return {
    firstMessage: GUIDE_FIRST_MESSAGE,
    clientMessages: ['speech-update', 'transcript', 'hang', 'tool-calls'],
    model: {
      provider: 'openai',
      model: 'gpt-4o-mini',
      messages: [{ role: 'system', content: GUIDE_SYSTEM_PROMPT }],
    },
  };
}

class RoomGuide484 {
  constructor() {
    this.modeView = null;
    this.vapi = null;
    this.publicKey = null;
    this.assistantId = null;
    this.configReady = false;
    this.configPromise = null;
    this.callActive = false;
    this.connecting = false;
    this.sessionId = 0;
    this._mediaStream = null;
    this._tourStep = TOUR_STEP.GUEST_SEAT;
    this._infoAdvanceTimer = null;
  }

  isTargetWorld(modeView) {
    const id = modeView?.worldID
      ?? modeView?.model?.worldID
      ?? modeView?.model?.config?.worldId;
    return String(id ?? '') === WORLD_ID;
  }

  /**
   * Called after bagless roomEntered for the current scene.
   * Only runs for world 484.
   */
  async onRoomEntered(modeView) {
    if (!modeView || modeView.editMode || !this.isTargetWorld(modeView)) return;
    this.modeView = modeView;
    this.stop({ silent: true });
    await this.start();
  }

  /** Stop the guide when leaving the room / changing scene. */
  onSceneLeaving(modeView) {
    if (!this.callActive && !this.connecting && !this.vapi) return;
    if (modeView && !this.isTargetWorld(modeView) && this.modeView !== modeView) return;
    this.stop();
  }

  async ensureConfig() {
    if (this.configReady) return true;
    if (this.configPromise) return this.configPromise;

    this.configPromise = (async () => {
      let publicKey = null;
      let assistantId = null;

      try {
        if (ENCRYPTED_CONFIG?.publicKey) {
          publicKey = '5a62573d-26fd-4126-9c12-e0a37cc53e12';
        }
        if (ENCRYPTED_CONFIG?.assistantId) {
          assistantId = 'ccc376e3-d0bb-409a-a3d3-004356060adc';
        }
      } catch (_) { /* ignore */ }

      if (isPlaceholderKey(publicKey)) {
        console.warn('[RoomGuide484] VAPI public key missing or invalid');
        this.configReady = false;
        return false;
      }

      this.publicKey = publicKey;
      this.assistantId = assistantId || null;
      this.configReady = true;
      return true;
    })();

    return this.configPromise;
  }

  async start() {
    if (this.connecting || this.callActive) return;
    const ok = await this.ensureConfig();
    if (!ok || !this.modeView) return;

    this.connecting = true;
    this._tourStep = TOUR_STEP.GUEST_SEAT;
    this._clearInfoAdvanceTimer();
    const sessionId = ++this.sessionId;

    const mediaOk = await this._requestMic();
    if (!this._isCurrentSession(sessionId)) return;
    if (!mediaOk) {
      this.connecting = false;
      this._showStartOverlay();
      return;
    }

    this._hideStartOverlay();
    this._stopMedia();
    await this._ensureVapi();
    if (!this.vapi) {
      this.connecting = false;
      return;
    }

    try {
      if (this.assistantId) {
        this.vapi.start(this.assistantId, getAssistantOverrides());
      } else {
        this.vapi.start(getInlineAssistant());
      }
    } catch (err) {
      console.error('[RoomGuide484] vapi.start failed', err);
      this.connecting = false;
      this._stopMedia();
    }
  }

  stop({ silent = false } = {}) {
    this.sessionId += 1;
    this.connecting = false;
    this.callActive = false;
    this._tourStep = TOUR_STEP.GUEST_SEAT;
    this._clearInfoAdvanceTimer();
    this._hideStartOverlay();
    this._stopMedia();
    if (this.vapi) {
      try {
        this.vapi.stop();
      } catch (err) {
        if (!silent) console.warn('[RoomGuide484] vapi.stop', err);
      }
    }
  }

  _isCurrentSession(sessionId) {
    return sessionId === this.sessionId;
  }

  _clearInfoAdvanceTimer() {
    if (this._infoAdvanceTimer != null) {
      clearTimeout(this._infoAdvanceTimer);
      this._infoAdvanceTimer = null;
    }
  }

  async _ensureVapi() {
    if (this.vapi || isPlaceholderKey(this.publicKey)) return;
    await releasePreloadedVapiWebRtc();
    const vapi = new Vapi(this.publicKey);
    this.vapi = vapi;

    vapi.on('call-start', () => {
      this.connecting = false;
      this.callActive = true;
      this._tourStep = TOUR_STEP.GUEST_SEAT;
      this._lookAtObject(GUEST_SEAT_ID, 'guestSeat');
    });

    vapi.on('speech-update', (message) => {
      this._onSpeechUpdate(message);
    });

    vapi.on('message', (message) => {
      if (message?.type === 'speech-update') {
        this._onSpeechUpdate(message);
      }
    });

    vapi.on('call-end', () => {
      this.connecting = false;
      this.callActive = false;
      this._tourStep = TOUR_STEP.GUEST_SEAT;
      this._clearInfoAdvanceTimer();
      this._stopMedia();
      this._hideStartOverlay();
    });

    vapi.on('call-start-failed', () => {
      this.connecting = false;
      this.callActive = false;
      this._clearInfoAdvanceTimer();
      this._stopMedia();
      this._showStartOverlay();
    });

    vapi.on('error', (err) => {
      const normalEnd = err?.type === 'daily-error'
        && (err?.error?.type === 'ejected' || err?.error?.msg === 'Meeting has ended');
      this.connecting = false;
      this.callActive = false;
      this._clearInfoAdvanceTimer();
      this._stopMedia();
      if (!normalEnd) {
        console.error('[RoomGuide484] Vapi error', err);
        this._showStartOverlay();
      }
    });
  }

  _onSpeechUpdate(message) {
    if (!this.callActive || !message) return;
    const status = message.status;
    const role = message.role;
    if (role !== 'assistant' || status !== 'stopped') return;
    if (this._tourStep !== TOUR_STEP.GUEST_SEAT) return;

    this._tourStep = TOUR_STEP.INFO_ELEMENT;
    this._advanceToInfoElement();
  }

  _advanceToInfoElement() {
    const sessionId = this.sessionId;
    this._lookAtObject(INFO_ELEMENT_ID, 'infoElement');
    this._clearInfoAdvanceTimer();
    this._infoAdvanceTimer = setTimeout(() => {
      this._infoAdvanceTimer = null;
      if (!this._isCurrentSession(sessionId) || !this.callActive || !this.vapi) return;
      try {
        this.vapi.say(INFO_ELEMENT_MESSAGE);
      } catch (err) {
        console.warn('[RoomGuide484] vapi.say failed', err);
      }
      this._tourStep = TOUR_STEP.DONE;
    }, Math.round(LOOK_AT_DURATION * 1000));
  }

  _lookAtObject(objectId, label) {
    const view = this.modeView;
    if (!view || typeof view.find3dObject !== 'function' || typeof view.animateCameraLookAt !== 'function') {
      return;
    }
    const mesh = view.find3dObject(objectId);
    const parent = mesh?.parent || null;
    if (!parent) {
      console.warn(`[RoomGuide484] ${label} not found`, objectId);
      return;
    }
    view.animateCameraLookAt(parent, LOOK_AT_DURATION);
  }

  async _requestMic() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      this._mediaStream = stream;
      return true;
    } catch (err) {
      console.warn('[RoomGuide484] mic permission failed', err);
      return false;
    }
  }

  _stopMedia() {
    if (this._mediaStream) {
      this._mediaStream.getTracks().forEach((t) => t.stop());
      this._mediaStream = null;
    }
  }

  _showStartOverlay() {
    if (typeof document === 'undefined') return;
    this._hideStartOverlay();
    const btn = document.createElement('button');
    btn.id = OVERLAY_ID;
    btn.type = 'button';
    btn.textContent = 'Start room guide';
    Object.assign(btn.style, {
      position: 'fixed',
      left: '50%',
      bottom: '28px',
      transform: 'translateX(-50%)',
      zIndex: '10050',
      padding: '12px 20px',
      border: 'none',
      borderRadius: '8px',
      background: '#1a1a1a',
      color: '#fff',
      fontSize: '15px',
      cursor: 'pointer',
      boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
    });
    btn.addEventListener('click', () => {
      this._hideStartOverlay();
      this.start();
    });
    document.body.appendChild(btn);
  }

  _hideStartOverlay() {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(OVERLAY_ID);
    if (el) el.remove();
  }
}

export const roomGuide484 = new RoomGuide484();
export default roomGuide484;
